import { useState, useCallback } from 'react';
import { useCreateVendor, useVendors } from '../../hooks/useVendors';
import { useDuplicateNameGuard, isSameName } from '../../hooks/useDuplicateNameGuard';
import { GoogleAddressAutocomplete } from '../common/GoogleAddressAutocomplete';

interface CreateVendorPanelProps {
  onClose: () => void;
  onCreated: () => void;
}

interface FormState {
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  taxId: string;
  website: string;
  notes: string;
}

const initialForm: FormState = {
  businessName: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  taxId: '',
  website: '',
  notes: '',
};

export function CreateVendorPanel({ onClose, onCreated }: CreateVendorPanelProps) {
  const createVendor = useCreateVendor();
  const { data: vendors } = useVendors({ isActive: true });
  const confirmDuplicate = useDuplicateNameGuard();
  const [form, setForm] = useState<FormState>(initialForm);
  const [error, setError] = useState('');

  const isValid = form.businessName.trim() !== '';

  const handleCreate = useCallback(async () => {
    setError('');
    const name = form.businessName.trim();
    const duplicate = (vendors ?? []).some((v) => isSameName(v.businessName, name));
    if (duplicate && !(await confirmDuplicate({ entityLabel: 'vendor', name }))) return;
    createVendor.mutate(
      {
        businessName: form.businessName.trim(),
        contactName: form.contactName.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
        city: form.city.trim() || undefined,
        state: form.state.trim() || undefined,
        zipCode: form.zipCode.trim() || undefined,
        country: form.country.trim() || undefined,
        taxId: form.taxId.trim() || undefined,
        website: form.website.trim() || undefined,
        notes: form.notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          onCreated();
        },
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to create vendor';
          setError(message);
        },
      },
    );
  }, [form, createVendor, onCreated, vendors, confirmDuplicate]);

  return (
    <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white overflow-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Create Vendor</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
      )}

      <div className="space-y-4">
        <div>
          <label
            htmlFor="create-vendor-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Business Name *
          </label>
          <input
            id="create-vendor-name"
            type="text"
            value={form.businessName}
            onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
            placeholder="Vendor business name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="create-vendor-contact"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Contact Name
          </label>
          <input
            id="create-vendor-contact"
            type="text"
            value={form.contactName}
            onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
            placeholder="Optional"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="create-vendor-email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              id="create-vendor-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="create-vendor-phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Phone
            </label>
            <input
              id="create-vendor-phone"
              type="text"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="create-vendor-address"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Address
          </label>
          <GoogleAddressAutocomplete
            id="create-vendor-address"
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onAddressSelect={(fields) =>
              setForm((f) => ({
                ...f,
                city: fields.city || f.city,
                state: fields.state || f.state,
                zipCode: fields.zipCode || f.zipCode,
                country: fields.country || f.country,
              }))
            }
            placeholder="Optional"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="create-vendor-city"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              City
            </label>
            <input
              id="create-vendor-city"
              type="text"
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="create-vendor-state"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              State
            </label>
            <input
              id="create-vendor-state"
              type="text"
              value={form.state}
              onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="create-vendor-zip"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              ZIP Code
            </label>
            <input
              id="create-vendor-zip"
              type="text"
              value={form.zipCode}
              onChange={(e) => setForm((f) => ({ ...f, zipCode: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="create-vendor-taxid"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Tax ID
            </label>
            <input
              id="create-vendor-taxid"
              type="text"
              value={form.taxId}
              onChange={(e) => setForm((f) => ({ ...f, taxId: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="create-vendor-country"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Country
          </label>
          <input
            id="create-vendor-country"
            type="text"
            value={form.country}
            onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
            placeholder="Optional"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="create-vendor-website"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Website
          </label>
          <input
            id="create-vendor-website"
            type="url"
            value={form.website}
            onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            placeholder="https://example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="create-vendor-notes"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Notes
          </label>
          <textarea
            id="create-vendor-notes"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Optional"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={handleCreate}
          disabled={!isValid || createVendor.isPending}
          className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {createVendor.isPending ? 'Creating...' : 'Create Vendor'}
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
